import { useQuery } from '@tanstack/react-query';
import { adminApi } from './admin-api.ts';
import { AdminShell, Card, PrimaryButton, Spinner, StatusChip, SourceTag, AssessmentTypeChip } from './ui.tsx';
import type { AdminUser, EngagementListItem } from './types.ts';

// Engagement picker → click a row to manage respondents / view analysis.
export function EngagementsList({
  admin,
  onOpen,
  onCreate,
  onLogout,
}: {
  admin: AdminUser;
  onOpen: (engagement: EngagementListItem) => void;
  onCreate: () => void;
  onLogout: () => void;
}) {
  const query = useQuery({ queryKey: ['admin-engagements'], queryFn: () => adminApi.engagements() });

  return (
    <AdminShell admin={admin} onLogout={onLogout}>
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink">Engagements</h1>
          <p className="mt-1 text-sm text-ink-soft">Select an engagement to manage respondents or view analysis.</p>
        </div>
        <PrimaryButton onClick={onCreate}>+ New engagement</PrimaryButton>
      </div>

      <Card>
        {query.isLoading ? (
          <Spinner />
        ) : query.isError ? (
          <p className="py-8 text-center text-sm text-red-600" role="alert">
            Could not load engagements.
          </p>
        ) : (
          <EngagementTable items={query.data ?? []} onOpen={onOpen} />
        )}
      </Card>
    </AdminShell>
  );
}

function EngagementTable({
  items,
  onOpen,
}: {
  items: EngagementListItem[];
  onOpen: (engagement: EngagementListItem) => void;
}) {
  if (items.length === 0) {
    return <p className="py-8 text-center text-sm text-ink-soft">No engagements yet.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-black/10 text-left text-xs uppercase tracking-wide text-ink-soft">
            <th scope="col" className="py-2 pr-4 font-semibold">Client</th>
            <th scope="col" className="py-2 pr-4 font-semibold">Engagement</th>
            <th scope="col" className="py-2 pr-4 font-semibold">Status</th>
            <th scope="col" className="py-2 pr-4 font-semibold">Source</th>
            <th scope="col" className="py-2 pr-4 font-semibold">Type</th>
            <th scope="col" className="py-2 pr-4 text-right font-semibold">Invited</th>
            <th scope="col" className="py-2 text-right font-semibold">Completed</th>
          </tr>
        </thead>
        <tbody>
          {items.map((e) => (
            <tr
              key={e.id}
              tabIndex={0}
              role="button"
              aria-label={`Open analysis for ${e.clientName} — ${e.internalName}`}
              onClick={() => onOpen(e)}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter' || ev.key === ' ') {
                  ev.preventDefault();
                  onOpen(e);
                }
              }}
              className="cursor-pointer border-b border-black/5 outline-none transition last:border-0 hover:bg-black/[0.02] focus-visible:bg-[var(--accent-soft)]"
            >
              <td className="py-3 pr-4 font-medium text-ink">{e.clientName}</td>
              <td className="py-3 pr-4 text-ink-soft">{e.internalName}</td>
              <td className="py-3 pr-4"><StatusChip status={e.status} /></td>
              <td className="py-3 pr-4"><SourceTag source={e.source} /></td>
              <td className="py-3 pr-4"><AssessmentTypeChip assessmentType={e.assessmentType} /></td>
              <td className="py-3 pr-4 text-right tabular-nums text-ink-soft">{e.invited}</td>
              <td className="py-3 text-right tabular-nums text-ink-soft">{e.completed}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
